import { PhoneOff, Ban, XCircle, Phone, ExternalLink, Zap, StopCircle, Pause, Play, SkipForward, Mic, MicOff, ChevronDown, ChevronRight, PhoneCall, User, MapPin, Mail, Calendar, Users, Clock, UserPlus, Hash, StickyNote, PhoneOutgoing, Loader2, Eye, EyeOff, PhoneForwarded } from "lucide-react";
import { InlineAddPartyPanel } from '@/app/_module/components/InlineAddPartyPanel';
import { useTwilioDevice } from "@/app/_module/hooks/useTwilioDevice";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";

import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { LeadDetailsPanel } from "@/app/(authenticated)/leads/_modules/components/LeadDetailsPanel";
import { InsuraABotKeyGrabber } from "@/app/(authenticated)/communication/_modules/components/InsuraABotKeyGrabber";
import { DTMFPollingWrapper, type DTMFPollingWrapperRef } from "@/app/(authenticated)/communication/_modules/components/DTMFPollingWrapper";
import { LeaveVoicemailModal } from "@/app/(authenticated)/communication/_modules/components/LeaveVoicemailModal";
import type { DTMFDigit } from "@/app/_module/services/dtmfService";
import { SendSmsModal } from "@/app/(authenticated)/communication/_modules/components/SendSmsModal";
import { SendEmailModal } from "@/app/(authenticated)/communication/_modules/components/SendEmailModal";
import { NewAppointmentDialog } from "@/app/(authenticated)/appointments/_modules/components/NewAppointmentDialog";
import { NewReminderDialog } from "@/app/(authenticated)/reminders/_modules/components/NewReminderDialog";
import { useUpdateLeadStatus, useLead } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import NoSaleReasonModal from "@/app/(authenticated)/leads/_modules/components/NoSaleReasonModal";
import { LeadStatus, LEAD_STATUS_DISPLAY_NAMES, getStatusDisplayName } from "@/app/_module/constants/leadStatus";
import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import { Progress } from "@/app/_module/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { useToast } from "@/app/_module/hooks/use-toast";
import InlineTransferPanel from "@/app/_module/components/InlineTransferPanel";
import { QuickDispositionButtons } from "@/app/_module/components/QuickDispositionButtons";
import { useDncConfirm } from "@/app/_module/hooks/useDncConfirm";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";
import { useDialBlocker } from "@/app/_module/hooks/useDialBlocker";
import { usePhoneNumberManager } from "@/app/_module/hooks/usePhoneNumberPricing";

import { PowerDialerBanner } from "./PowerDialerBanner";
import { PowerDialerControls } from "./PowerDialerControls";
import { usePowerDialerSession } from "./PowerDialerSessionLogic";
import { PowerDialerStats } from "./PowerDialerStats";
import { VoicemailPromptModal } from "./VoicemailPromptModal";
import { QuickNotesPanel } from "./QuickNotesPanel";
import { DialIQStatusCard } from "./DialIQStatusCard";
import { usePredictiveDial } from "@/app/_module/contexts/PredictiveDialContext";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { BulkAreaCodeRecommendationNotification } from "@/app/_module/components/BulkAreaCodeRecommendationNotification";
import { AudioDeviceSelector } from "@/app/_module/components/AudioDeviceSelector";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";
import { formatCallTimer } from "@/app/_module/utils/formatters";

interface InsuraABotKeyCode {
  id: string;
  code: string;
  timestamp: Date;
  duration: number;
  callId: string;
}

interface PowerDialerSessionProps {
  leads: Lead[];
  aiMatchingEnabled?: boolean;
  selectedAgentPhone?: string;
  onStop: () => void;
  onComplete: (stats?: { answered: number; totalDialTime: number; appointments?: number; underwriting?: number; sold?: number }) => void;
  hideLeadDetails?: boolean; // Hide lead details panel when embedded in widget
  autoOpenLeadDetails?: boolean; // Auto-open lead details when call is answered
}

// Status colors for compact dropdown
const STATUS_COLORS: Record<LeadStatus, string> = {
  [LeadStatus.NEW_LEAD]: "bg-blue-100 text-blue-800 hover:!bg-blue-200",
  [LeadStatus.APPOINTMENT]: "bg-cyan-600 text-cyan-100 hover:!bg-cyan-700",
  [LeadStatus.UWR]: "bg-amber-500 text-amber-100 hover:!bg-amber-600",
  [LeadStatus.APPROVED]: "bg-emerald-600 text-emerald-100 hover:!bg-emerald-700",
  [LeadStatus.NO_SALE]: "bg-red-600 text-red-100 hover:!bg-red-700",
  [LeadStatus.BAD_NUMBER]: "bg-red-500 text-red-100 hover:!bg-red-600",
  [LeadStatus.DO_NOT_CALL]: "bg-amber-800 text-amber-100 hover:!bg-amber-900",
  [LeadStatus.DISPOSED]: "bg-gray-600 text-gray-100 hover:!bg-gray-700",
  [LeadStatus.DECLINED]: "bg-rose-600 text-rose-100 hover:!bg-rose-700",
  [LeadStatus.LAPSED]: "bg-orange-600 text-orange-100 hover:!bg-orange-700",
};

export const PowerDialerSession = function ({
  leads,
  aiMatchingEnabled = false,
  selectedAgentPhone,
  onStop,
  onComplete,
  hideLeadDetails = false,
  autoOpenLeadDetails = false,
}: PowerDialerSessionProps) {
  const router = useRouter();
  const { toast } = useToast();
  const { user } = useUserContext();
  const updateLeadStatusMutation = useUpdateLeadStatus();
  const [insuraABotKeyCodes, setInsuraABotKeyCodes] = useState<InsuraABotKeyCode[]>([]);

  // Ref for DTMFPollingWrapper to clear digits
  const dtmfPollingRef = useRef<DTMFPollingWrapperRef>(null);

  // Phone number validation hook
  // Any dial blocker (payment past due, trial over, subscription ended, no number)
  // counts as "no usable number" for this session so it never auto-dials into a drop.
  const { blocked: dialBlocked, hasActiveNumbers: hasNumbers, isCheckingPhoneNumbers } = useDialBlocker();
  const hasActiveNumbers = hasNumbers && !dialBlocked;

  // Phone number manager for area codes
  const { phoneNumbers } = usePhoneNumberManager();

  // Derive user's owned area codes from phone numbers (handle both camelCase and snake_case from backend)
  const userAreaCodes = phoneNumbers
    .filter((phone: any) => phone.areaCode || phone.area_code)
    .map((phone: any) => phone.areaCode || phone.area_code)
    .filter((areaCode: string, index: number, self: string[]) => self.indexOf(areaCode) === index);

  // Modal states for quick actions
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showNoSaleModal, setShowNoSaleModal] = useState(false);
  const noSaleLeadIdRef = useRef<string | null>(null);

  // 3-Way Calling States
  const [showAddPartyPanel, setShowAddPartyPanel] = useState(false);
  const [loadingParticipantSid, setLoadingParticipantSid] = useState<string | null>(null);

  // Quick Notes panel state
  const [showQuickNotes, setShowQuickNotes] = useState(false);

  const {
    currentIndex,
    currentLead,
    isDialing,
    isPaused,
    answeredCount,
    failedCount,
    skippedCount,
    voicemailCount,
    dispositionCount,
    isCallAnswered,
    currentCallId,
    dropVoicemailAdvance,
    isCallRecording,
    isDTMFListening,
    setIsDTMFListening,
    isSpeechProcessing,
    setIsSpeechProcessing,
    hasMicrophone,
    // Voicemail handling
    showVoicemailPrompt,
    setShowVoicemailPrompt,
    showVoicemailModal,
    setShowVoicemailModal,
    // Session timing
    getDialSeconds,
    handleCall,
    handleSkip,
    handleMarkFailed,
    handlePause,
    handleResume,
    handleStopSession, // Stop session - disconnects active call and ends session
    handleRecordingToggle,
    trackLeadStatus, // Track lead status for session stats
    // Twilio Device values
    isDeviceReady,
    activeCall,
    activeCallSid,
    disconnect,
    handleEndCall, // End call and auto-advance to next lead
    mute,
    unmute,
    hold,
    unhold,
    isMuted,
    isOnHold,
    callStatus,
    appleScreeningDetected,
    callDuration,
    microphoneAudioLevel,
    isHardwareMuted,
    audioInputDevices,
    audioOutputDevices,
    selectedInputDeviceId,
    selectedOutputDeviceId,
    setAudioInputDevice,
    setAudioOutputDevice,
    refreshAudioDevices,
    // Audio gain/volume controls
    microphoneGain,
    speakerVolume,
    setMicrophoneGain,
    setSpeakerVolume,
    // Double-dial retry state
    isRetryAttempt,
    // Disposition pause after 60s+ connected calls
    awaitingDisposition,
    handleSkipDisposition,
    // Failure toast
    failureToast,
    setFailureToast,
  } = usePowerDialerSession({ leads, aiMatchingEnabled, selectedAgentPhone, onComplete, hasActiveNumbers, autoStartFirstCall: true });

  // 3-Way Calling from TwilioDevice context
  const {
    conferenceParticipants,
    isAddingParticipant,
    addParticipantToCall,
    removeParticipantFromCall,
    muteParticipant,
    holdParticipant,
    setActiveConference,
    sendDigits,
    callMetadata,
    startAudioMonitoring,
    stopAudioMonitoring,
  } = useTwilioDevice();

  // ─── DialIQ Predictive Dial integration ────────────────────────────────────
  // When the dialer was opened via DialIQ, we ask the backend to start a
  // predictive session. If predictive is globally disabled (env var off), the
  // backend returns a "disabled" response and PredictiveDialContext silently
  // stays inactive — agent gets standard PowerDialer with no banner/sidebar.
  //
  // Per-session abandonment auto-throttle (2%/2.5%) is the TCPA safety net.
  const { dialerSource } = useUnifiedDialer();
  const predictive = usePredictiveDial();

  useEffect(() => {
    if (dialerSource !== 'dialiq') return;
    if (predictive.isPredictiveActive) return;
    // Fire-and-forget. Backend may return null/disabled — context handles it.
    predictive.startSession('dialiq').catch(() => { /* silent fallback */ });
    return () => {
      // End the session when PowerDialer unmounts (session done / agent navigated away)
      if (predictive.isPredictiveActive) {
        predictive.endSession('completed').catch(() => { /* swallow */ });
      }
    };
    // We intentionally only run this once on mount + once on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialerSource]);

  // Dial pad state
  const [showDialPad, setShowDialPad] = useState(false);
  const [showCallerId, setShowCallerId] = useState(true);

  // Show toast when a lead fails validation (no phone, invalid number, connect error)
  useEffect(() => {
    if (failureToast) {
      toast({ title: failureToast, variant: "destructive", duration: 2000 });
      setFailureToast(null);
    }
  }, [failureToast, setFailureToast, toast]);

  // Spam prevention: visual cooldown on Skip and End buttons
  const [skipCooldown, setSkipCooldown] = useState(false);
  const [endCooldown, setEndCooldown] = useState(false);
  const skipCooldownTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const endCooldownTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (skipCooldownTimer.current) clearTimeout(skipCooldownTimer.current);
      if (endCooldownTimer.current) clearTimeout(endCooldownTimer.current);
    };
  }, []);

  const handleSkipWithCooldown = useCallback(() => {
    if (skipCooldown) return;
    setSkipCooldown(true);
    handleSkip();
    skipCooldownTimer.current = setTimeout(() => setSkipCooldown(false), 1500);
  }, [handleSkip, skipCooldown]);

  const handleEndWithCooldown = useCallback(() => {
    if (endCooldown) return;
    setEndCooldown(true);
    handleEndCall();
    endCooldownTimer.current = setTimeout(() => setEndCooldown(false), 1500);
  }, [handleEndCall, endCooldown]);

  // ── Manual "Drop Voicemail" ──────────────────────────────────
  // Plays the resolved drop (tag → stage → source → default) on the active
  // call and hangs up — same primitive as auto-drop — then advances like End
  // Call. For the case AMD misses. Gated on the agent having ≥1 drop.
  const [hasVoicemailDrops, setHasVoicemailDrops] = useState(false);
  const [droppingVoicemail, setDroppingVoicemail] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<any>({ url: getApiUrl("/communication/voicemail-drops"), auth: true });
        const drops = r?.data?.data || r?.data || [];
        if (!cancelled) setHasVoicemailDrops(Array.isArray(drops) && drops.length > 0);
      } catch { /* leave hidden on error */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const handleDropVoicemail = useCallback(() => {
    // Target the CUSTOMER leg (currentCallId), NOT activeCallSid — the latter is
    // the agent's Twilio Device / persistent-conference leg, and dropping there
    // would play the VM to the agent instead of the customer's mailbox.
    if (!currentCallId || droppingVoicemail) return;
    const cid = currentCallId; // capture — the advance below clears currentCallId
    setDroppingVoicemail(true);
    // FIRE-AND-FORGET: don't await. The backend plays the VM + hangs up the
    // customer leg on its own.
    api.post({
      url: getApiUrl(`/communication/calls/${cid}/drop-voicemail`),
      body: { leadId: currentLead?.id },
      auth: true,
    })
      .catch(() => {
        toast({ title: "Voicemail drop may have failed", variant: "destructive", duration: 3000 });
      })
      .finally(() => setDroppingVoicemail(false));
    toast({ title: "Voicemail dropping in background", duration: 2000 });
    // INSTANT advance — moves to the next lead now, WITHOUT hanging up the
    // customer leg (it's playing the VM in the background via the drop's Hangup).
    dropVoicemailAdvance();
  }, [currentCallId, currentLead?.id, droppingVoicemail, toast, dropVoicemailAdvance]);

  // Expandable lead info (starts minimized, user can toggle, stays in whatever state through call)
  const [showLeadInfo, setShowLeadInfo] = useState(false);
  const [showTransferPanel, setShowTransferPanel] = useState(false);
  const onDirect130 = useDirect130Host(); // owner 2026-11-02: no call transfer on Direct 130's address

  // Fetch full lead details when active (same as manual dialer)
  // The leads from selectedLeads may not have all fields populated
  const { data: fetchedLeadData } = useLead(currentLead?.id || '');
  const currentLeadDetails = useMemo(() => {
    if (!currentLead) return null;
    if (!fetchedLeadData) return currentLead;
    // Extract lead from response - useLead returns { data: Lead, message: string }
    const fetched = ('data' in fetchedLeadData && fetchedLeadData.data)
      ? (fetchedLeadData.data as Lead)
      : ('name' in fetchedLeadData)
        ? (fetchedLeadData as unknown as Lead)
        : null;
    if (!fetched) return currentLead;
    // Merge: fetched data takes priority, fallback to base lead
    return { ...currentLead, ...fetched };
  }, [currentLead, fetchedLeadData]);

  // Reset lead info to minimized when moving to next lead
  useEffect(() => {
    setShowLeadInfo(false);
  }, [currentLead?.id]);

  // Call status badge helpers (matches ActiveCallWidget pattern)
  const getCallStatusColor = (status: string) => {
    switch (status) {
      case "queued":
      case "initiated":
        return "bg-yellow-100 text-yellow-800";
      case "ringing":
      case "connecting":
        return "bg-blue-100 text-blue-800";
      case "answered":
      case "in-progress":
        return "bg-green-100 text-green-800";
      case "voicemail":
        return "bg-purple-100 text-purple-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCallStatusText = (status: string) => {
    switch (status) {
      case "queued": return "Queued";
      case "initiated": return "Initiating...";
      case "ringing": return "Ringing...";
      case "connecting": return "Ringing...";
      case "answered":
      case "in-progress": return "Answered";
      case "voicemail": return "Voicemail";
      case "completed": return "Call Ended";
      case "failed": return "Failed";
      case "busy": return "Busy";
      case "no-answer":
      case "no_answer": return "No Answer";
      default: return status || "Dialing...";
    }
  };

  // Track if we've already opened lead details for this lead to prevent duplicate opens
  const autoOpenedForLeadRef = useRef<string | null>(null);

  // Auto-open lead details whenever Power Dialer starts ringing the next lead.
  //
  // The `autoOpenLeadDetails` user preference is intentionally ignored here.
  // Same reasoning as the multi-line dialer (MultiLineDialerSession.tsx): in
  // an auto-advance dialer, the agent CAN'T predict who will be ringing next,
  // so the profile panel must follow the call. Ouza's report: power dialer
  // auto-advances but the visible page stayed on whoever was open when the
  // session started, so agents talked to the next lead with the wrong notes.
  // We only navigate once per lead transition (autoOpenedForLeadRef guard).
  useEffect(() => {
    if (isDialing && currentLead?.id) {
      if (autoOpenedForLeadRef.current !== currentLead.id) {
        autoOpenedForLeadRef.current = currentLead.id;
        router.push(`/leads/${currentLead.id}`);
      }
    }
    // Reset tracking when we move to next lead (currentLead changes) and not dialing
    if (!isDialing && !activeCallSid && currentLead?.id !== autoOpenedForLeadRef.current) {
      autoOpenedForLeadRef.current = null;
    }
  }, [isDialing, currentLead?.id, activeCallSid, router]);

  // Handle DTMF updates from polling wrapper (customer keypresses)
  const handleDTMFUpdate = useCallback((digits: DTMFDigit[]) => {
    if (!activeCallSid) {

      return;
    }

    // Convert DTMFDigit[] to InsuraABotKeyCode[] format
    const convertedCodes: InsuraABotKeyCode[] = digits.map((digit, digitIndex) => ({
      // Index is the tiebreaker: timestamp.toString() is only SECOND-resolution, so
      // two digits pressed inside the same second produced identical React keys
      // ("Encountered two children with the same key"). The digit list is
      // append-only, so the index is stable across renders.
      id: `${activeCallSid}-${digit.timestamp.toString()}-${digitIndex}`,
      code: digit.digit,
      timestamp: new Date(digit.timestamp),
      duration: digit.callDuration || 0,
      callId: activeCallSid,
    }));

    // Replace all codes with the new set from backend
    setInsuraABotKeyCodes(convertedCodes);
  }, [activeCallSid]);

  // Callback to clear codes when InsuraABotKeyGrabber starts a new collection
  const handleClearCodes = useCallback(() => {
    setInsuraABotKeyCodes([]);
    dtmfPollingRef.current?.clearDigits();
  }, []);

  // 3-Way Calling Handlers
  const handleAddParticipant = async (phoneNumber: string, name?: string, role?: 'agent' | 'customer' | 'consultant') => {
    const success = await addParticipantToCall(phoneNumber, name, role);
    return success;
  };

  const handleMuteToggle = async (participantSid: string, currentlyMuted: boolean) => {
    setLoadingParticipantSid(participantSid);
    await muteParticipant(participantSid, !currentlyMuted);
    setLoadingParticipantSid(null);
  };

  const handleHoldToggle = async (participantSid: string, currentlyOnHold: boolean) => {
    setLoadingParticipantSid(participantSid);
    await holdParticipant(participantSid, !currentlyOnHold);
    setLoadingParticipantSid(null);
  };

  const handleRemoveParticipant = async (participantSid: string) => {
    setLoadingParticipantSid(participantSid);
    await removeParticipantFromCall(participantSid);
    setLoadingParticipantSid(null);
  };

  // DNC asks first (and offers to block their calls and texts). The dialer can move on while
  // that dialog is open, so these say where it is NOW.
  const { confirmDnc, modal: dncModal } = useDncConfirm();
  const liveLeadIdRef = useRef<string | undefined>(undefined);
  const markFailedRef = useRef(handleMarkFailed);
  useEffect(() => {
    liveLeadIdRef.current = currentLead?.id;
    markFailedRef.current = handleMarkFailed;
  });

  // Handler for updating lead status. False when nothing was filed (no lead, No Sale reason
  // pending, DNC backed out of).
  const handleStatusUpdate = async (status: string, noSaleReason?: string) => {
    const leadId = currentLead?.id || noSaleLeadIdRef.current;
    if (!leadId) {
      toast({
        title: "Error",
        description: "No lead selected",
        variant: "destructive",
      });
      return false;
    }

    // Intercept No Sale to show reason modal
    if (status === LeadStatus.NO_SALE && !noSaleReason) {
      noSaleLeadIdRef.current = leadId; // Capture lead ID before modal opens
      setShowNoSaleModal(true);
      return false;
    }

    let blockInbound = false;
    if (status === LeadStatus.DO_NOT_CALL) {
      const answer = await confirmDnc();
      if (answer === null) return false;
      blockInbound = answer;
    }

    try {
      await updateLeadStatusMutation.mutateAsync({
        leadId,
        status: status,
        ...(noSaleReason ? { noSaleReason } : {}),
        ...(blockInbound ? { blockInbound } : {}),
      });

      // Track lead status for session stats (Appt, Underwriting, Sold & Approved)
      trackLeadStatus(leadId, status);

      toast({
        title: "Status Updated",
        description: `Lead status updated to ${status}`,
        variant: "default",
      });
    } catch (error) {

      toast({
        title: "Update Failed",
        description: "Failed to update lead status. Please try again.",
        variant: "destructive",
      });
    } finally {
      if (status === LeadStatus.NO_SALE) {
        noSaleLeadIdRef.current = null;
      }
    }
    return true;
  };

  // DNC and the agent's own buttons: file the lead, then on to the next one, like Bad #.
  // Not if the dialer already moved on while the DNC dialog was open.
  const fileAndNext = async (status: string) => {
    const leadId = currentLead?.id;
    if (!(await handleStatusUpdate(status))) return;
    if (leadId && liveLeadIdRef.current === leadId) markFailedRef.current();
  };

  const handleNoSaleConfirm = async (reason: string) => {
    await handleStatusUpdate(LeadStatus.NO_SALE, reason);
    handleMarkFailed();
  };

  // Format phone number
  const formatPhone = (phone: string): string => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Format call duration
  const formatDuration = (seconds: number) => formatCallTimer(seconds);

  // Calculate progress
  const progress = leads.length > 0 ? (currentIndex / leads.length) * 100 : 0;

  // If currentLead is undefined, either no leads or session is complete
  if (!currentLead) {
    // If we have leads but currentLead is undefined, session is complete
    if (leads.length > 0 && currentIndex >= leads.length) {
      return (
        <>
          <div className={hideLeadDetails ? "p-3" : "max-w-6xl mx-auto"}>
            <PowerDialerStats
              successCount={answeredCount}
              totalDialTime={getDialSeconds()}
            />
          </div>
          <NoSaleReasonModal
            isOpen={showNoSaleModal}
            onOpenChange={setShowNoSaleModal}
            onConfirm={handleNoSaleConfirm}
          />
          {dncModal}
        </>
      );
    }
    // Otherwise, leads haven't loaded yet or something is wrong - return null to not render anything
    // The parent component should handle this case
    return (
      <>
        <NoSaleReasonModal
          isOpen={showNoSaleModal}
          onOpenChange={setShowNoSaleModal}
          onConfirm={handleNoSaleConfirm}
        />
        {dncModal}
      </>
    );
  }

  // Compact Widget View
  if (hideLeadDetails) {
    return (
      <div className="p-3 space-y-3">
        {/* DialIQ session identity — shows the queued next lead inline when one is in flight */}
        {predictive.isPredictiveActive && (
          <DialIQStatusCard
            predialState={predictive.predialState}
            predialLead={predictive.predialLead}
            isThrottleKilled={predictive.isThrottleKilled}
            compact
          />
        )}

        {/* Compact Progress Header */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-primary" />
              <span className="font-semibold text-xs text-primary">Live Session</span>
              <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
              {isPaused && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-amber-100 text-amber-700 border-0">Paused</Badge>}
            </div>
            <div className="flex items-center gap-1">
              <Button
                onClick={isPaused ? handleResume : handlePause}
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
              >
                {isPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              </Button>
              <Button
                onClick={handleStopSession}
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <StopCircle className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          <div className="text-xs text-gray-600 mb-1.5">
            {isRetryAttempt ? (
              <>Retrying Lead {currentIndex + 1} of {leads.length} • {Math.round(progress)}% Complete</>
            ) : (
              <>Lead {currentIndex + 1} of {leads.length} • {Math.round(progress)}% Complete</>
            )}
          </div>
          <Progress className="h-1.5" value={progress} />

          {/* Compact Stats */}
          <div className="grid grid-cols-4 gap-1 mt-2 text-center">
            <div className="bg-white rounded px-1.5 py-1 border">
              <div className="text-sm font-bold text-green-600">{answeredCount}</div>
              <div className="text-[9px] text-muted-foreground">Answered</div>
            </div>
            <div className="bg-white rounded px-1.5 py-1 border">
              <div className="text-sm font-bold text-purple-600">{voicemailCount}</div>
              <div className="text-[9px] text-muted-foreground">VM</div>
            </div>
            <div className="bg-white rounded px-1.5 py-1 border">
              <div className="text-sm font-bold text-red-600">{failedCount}</div>
              <div className="text-[9px] text-muted-foreground">Bad</div>
            </div>
            <div className="bg-white rounded px-1.5 py-1 border">
              <div className="text-sm font-bold text-gray-600">{skippedCount}</div>
              <div className="text-[9px] text-muted-foreground">Skip</div>
            </div>
          </div>
        </div>

        {/* Current Lead Card */}
        <div className="bg-white border rounded-lg overflow-hidden">
          {/* Header with avatar, name, and duration (when on call) */}
          <div className="px-3 py-2.5 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-white">
                <User className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate text-sm">{currentLead.name}</h3>
                  {(activeCall || isDialing) ? (
                    <Badge className={`text-[9px] px-1.5 py-0 h-4 border-0 flex-shrink-0 ${getCallStatusColor(callStatus || (isDialing ? 'ringing' : ''))}`}>
                      {getCallStatusText(callStatus || (isDialing ? 'ringing' : ''))}
                    </Badge>
                  ) : (currentLead.leadStat || currentLead.status) ? (
                    <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4 bg-blue-100 text-blue-700 border-0 flex-shrink-0">
                      {getStatusDisplayName(currentLead.leadStat || currentLead.status)}
                    </Badge>
                  ) : null}
                </div>
                <div className="flex items-center gap-1">
                  <p className="text-xs text-gray-500">{formatPhone(currentLead.phone || '')}</p>
                  {callMetadata?.outboundCallerId && (
                    <button
                      onClick={() => setShowCallerId(!showCallerId)}
                      className={`p-0.5 rounded ${showCallerId ? 'text-emerald-600 hover:text-emerald-700' : 'text-gray-400 hover:text-emerald-600'} transition-colors`}
                      title={showCallerId ? 'Hide outbound caller ID' : 'Show outbound caller ID'}
                    >
                      {showCallerId ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </button>
                  )}
                </div>
                {showCallerId && callMetadata?.outboundCallerId && (
                  <p className="text-xs text-emerald-600 leading-normal font-medium">From: {formatPhone(callMetadata.outboundCallerId)}</p>
                )}
              </div>
              {/* Call duration shown inline when on active call */}
              {activeCall && (
                <div className="text-sm font-mono text-gray-700 flex-shrink-0">
                  {formatDuration(callDuration)}
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                className={`h-7 w-7 p-0 flex-shrink-0 ${showQuickNotes ? 'text-amber-600 bg-amber-50' : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'}`}
                onClick={() => setShowQuickNotes(!showQuickNotes)}
                title="Quick Notes"
              >
                <StickyNote className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Apple/Google Call Screening detected — dedicated row, full width,
              pulses to grab agent attention. Agent must say their name + reason
              to navigate the screener. Triggered on AMD machine_end_other for
              BOTH standard PowerDialer and DialIQ predictive. */}
          {appleScreeningDetected && (activeCall || isDialing) && (
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2 animate-pulse">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
              <span className="text-[11px] uppercase tracking-wider font-bold text-amber-900">
                Potential Call Screening
              </span>
            </div>
          )}

          {/* Quick Notes Panel */}
          {showQuickNotes && currentLead && (
            <div className="px-3 py-2 border-b border-gray-100">
              <QuickNotesPanel
                leadId={currentLead.id}
                leadName={currentLead.name}
                onClose={() => setShowQuickNotes(false)}
              />
            </div>
          )}

          {/* Lead Details - expandable toggle, starts minimized */}
          <button
            onClick={() => setShowLeadInfo(!showLeadInfo)}
            className="w-full px-3 py-1.5 flex items-center gap-1.5 text-xs text-gray-500 hover:bg-gray-50 border-b border-gray-100 transition-colors"
          >
            {showLeadInfo ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            <span>Lead Info</span>
          </button>

          {showLeadInfo && currentLeadDetails && (
            <div className="px-3 py-2 border-b border-gray-100">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {/* Email */}
                {currentLeadDetails.email && (
                  <div className="text-center">
                    <span className="text-gray-400">Email</span>
                    <p className="text-gray-700 truncate">{currentLeadDetails.email}</p>
                  </div>
                )}

                {/* Age */}
                {currentLeadDetails.age && (
                  <div className="text-center">
                    <span className="text-gray-400">Age</span>
                    <p className="text-gray-700">{currentLeadDetails.age} years</p>
                  </div>
                )}

                {/* DOB */}
                {(currentLeadDetails.dob || currentLeadDetails.birthday) && (
                  <div className="text-center">
                    <span className="text-gray-400">DOB</span>
                    <p className="text-gray-700">
                      {(() => {
                        const dobStr = currentLeadDetails.dob || currentLeadDetails.birthday;
                        try {
                          const [year, month, day] = dobStr!.split('T')[0].split('-').map(Number);
                          return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
                        } catch {
                          return dobStr;
                        }
                      })()}
                    </p>
                  </div>
                )}

                {/* Gender */}
                {currentLeadDetails.gender && (
                  <div className="text-center">
                    <span className="text-gray-400">Gender</span>
                    <p className="text-gray-700 capitalize">{currentLeadDetails.gender}</p>
                  </div>
                )}

                {/* Marital Status */}
                {(currentLeadDetails.marital || currentLeadDetails.maritalStatus) && (
                  <div className="text-center">
                    <span className="text-gray-400">Marital</span>
                    <p className="text-gray-700 capitalize">{currentLeadDetails.marital || currentLeadDetails.maritalStatus}</p>
                  </div>
                )}

                {/* Height */}
                {currentLeadDetails.height && (
                  <div className="text-center">
                    <span className="text-gray-400">Height</span>
                    <p className="text-gray-700">{currentLeadDetails.height}</p>
                  </div>
                )}

                {/* Weight */}
                {currentLeadDetails.weight && (
                  <div className="text-center">
                    <span className="text-gray-400">Weight</span>
                    <p className="text-gray-700">{currentLeadDetails.weight} lbs</p>
                  </div>
                )}

                {/* Address */}
                {currentLeadDetails.address && (
                  <div className="text-center">
                    <span className="text-gray-400">Address</span>
                    <p className="text-gray-700 break-words">{currentLeadDetails.address}</p>
                  </div>
                )}

                {/* State/Zip */}
                {(currentLeadDetails.state || currentLeadDetails.zipCode) && (
                  <div className="text-center">
                    <span className="text-gray-400">State/Zip</span>
                    <p className="text-gray-700">{[currentLeadDetails.state, currentLeadDetails.zipCode].filter(Boolean).join(' ')}</p>
                  </div>
                )}

                {/* City - full width centered */}
                {currentLeadDetails.city && (
                  <div className="col-span-2 text-center">
                    <span className="text-gray-400">City</span>
                    <p className="text-gray-700">{currentLeadDetails.city}</p>
                  </div>
                )}

                {/* Employment - full width centered */}
                {(currentLeadDetails.employer || currentLeadDetails.employmentStatus) && (
                  <div className="col-span-2 text-center">
                    <span className="text-gray-400">Employment</span>
                    <p className="text-gray-700 break-words">{currentLeadDetails.employer || currentLeadDetails.employmentStatus}</p>
                  </div>
                )}
              </div>

              {/* List name badge */}
              {currentLeadDetails.listName && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {currentLeadDetails.listName}
                  </span>
                </div>
              )}

              {/* Fallback when no fields available */}
              {!currentLeadDetails.email &&
               !currentLeadDetails.dob &&
               !currentLeadDetails.birthday &&
               !currentLeadDetails.age &&
               !currentLeadDetails.gender &&
               !currentLeadDetails.marital &&
               !currentLeadDetails.maritalStatus &&
               !currentLeadDetails.height &&
               !currentLeadDetails.weight &&
               !currentLeadDetails.address &&
               !currentLeadDetails.city &&
               !currentLeadDetails.state &&
               !currentLeadDetails.zipCode &&
               !currentLeadDetails.employer &&
               !currentLeadDetails.employmentStatus &&
               !currentLeadDetails.listName && (
                <p className="text-xs text-gray-400 italic">No additional details available</p>
              )}
            </div>
          )}

          {/* Call Status Indicator removed - duration timer already shows call is active */}

          {/* Controls section */}
          <div className="p-3 pt-2 space-y-2">
            {!activeCall ? (
              /* Pre-call controls - Speed dial mode
                 No End button here — End only shows after Twilio connection is established.
                 During dialing: only Skip is shown so agent can move on.
                 When idle: Pause/Resume + Skip. */
              <div className={`grid ${isDialing ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
                {isDialing ? null : awaitingDisposition ? (
                  /* After 60s+ call - show Next Lead button instead of Pause */
                  <Button
                    className="bg-green-600 hover:bg-green-700 h-10"
                    onClick={handleSkipDisposition}
                  >
                    <SkipForward className="h-4 w-4 mr-1.5" />
                    Next Lead
                  </Button>
                ) : isPaused ? (
                  /* Paused - show Resume button */
                  <Button
                    className="bg-green-600 hover:bg-green-700 h-10"
                    onClick={handleResume}
                  >
                    <Play className="h-4 w-4 mr-1.5" />
                    Resume
                  </Button>
                ) : (
                  /* Active - show Pause button (auto-dial is running) */
                  <Button
                    className="bg-orange-500 hover:bg-orange-600 h-10"
                    onClick={handlePause}
                  >
                    <Pause className="h-4 w-4 mr-1.5" />
                    Pause
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="h-10"
                  onClick={handleSkipWithCooldown}
                  disabled={skipCooldown}
                >
                  {skipCooldown ? (
                    <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Skipping...</>
                  ) : (
                    <><SkipForward className="h-4 w-4 mr-1.5" />{isDialing ? "Skip Lead" : "Skip"}</>
                  )}
                </Button>
              </div>
            ) : (
              /* Active call controls */
              <div className="space-y-1.5">
                {/* Top row: Skip and End Call */}
                <div className="grid grid-cols-2 gap-1.5">
                  <Button
                    onClick={handleSkipWithCooldown}
                    size="sm"
                    variant="outline"
                    className="h-9 text-orange-600 border-orange-200 hover:bg-orange-50"
                    disabled={skipCooldown}
                  >
                    {skipCooldown ? (
                      <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Skipping...</>
                    ) : (
                      <><SkipForward className="h-4 w-4 mr-1" />Skip</>
                    )}
                  </Button>
                  <Button
                    onClick={handleEndWithCooldown}
                    size="sm"
                    variant="destructive"
                    className="h-9"
                    disabled={endCooldown}
                  >
                    {endCooldown ? (
                      <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Ending...</>
                    ) : (
                      <><PhoneOff className="h-4 w-4 mr-1" />End</>
                    )}
                  </Button>
                </div>
                {/* Bottom row: Mute, Hold, Dial Pad, Add Party */}
                <div className="grid grid-cols-4 gap-1.5">
                  <Button
                    onClick={isMuted ? unmute : mute}
                    size="sm"
                    variant={isMuted ? "default" : "outline"}
                    className={`h-9 ${isMuted ? "bg-yellow-600 hover:bg-yellow-700" : ""}`}
                  >
                    {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </Button>
                  <Button
                    onClick={() => isOnHold ? unhold() : hold()}
                    size="sm"
                    variant={isOnHold ? "default" : "outline"}
                    className={`h-9 ${isOnHold ? "bg-orange-600 hover:bg-orange-700" : ""}`}
                  >
                    {isOnHold ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                  </Button>
                  <Button
                    onClick={() => setShowDialPad(!showDialPad)}
                    size="sm"
                    variant={showDialPad ? "default" : "outline"}
                    className={`h-9 ${showDialPad ? "bg-purple-600 hover:bg-purple-700" : ""}`}
                    title="Dial Pad"
                  >
                    <Hash className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowAddPartyPanel(!showAddPartyPanel)}
                    className={`h-9 ${showAddPartyPanel ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}`}
                    title="Add Party"
                    disabled={!activeCall}
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Dial Pad */}
            {showDialPad && activeCall && (
              <div className="mt-2 p-2 border border-gray-200 rounded-lg bg-white">
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { digit: "1", letters: "" },
                    { digit: "2", letters: "ABC" },
                    { digit: "3", letters: "DEF" },
                    { digit: "4", letters: "GHI" },
                    { digit: "5", letters: "JKL" },
                    { digit: "6", letters: "MNO" },
                    { digit: "7", letters: "PQRS" },
                    { digit: "8", letters: "TUV" },
                    { digit: "9", letters: "WXYZ" },
                    { digit: "*", letters: "" },
                    { digit: "0", letters: "+" },
                    { digit: "#", letters: "" },
                  ].map(({ digit, letters }) => (
                    <Button
                      key={digit}
                      variant="outline"
                      size="sm"
                      className="h-10 flex flex-col justify-center hover:bg-gray-100"
                      onClick={() => sendDigits(digit)}
                    >
                      <span className="text-base font-semibold">{digit}</span>
                      {letters && <span className="text-[8px] text-gray-500">{letters}</span>}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* 3-Way Calling Inline Add Party Panel */}
            {showAddPartyPanel && activeCall && (
              <InlineAddPartyPanel
                participants={conferenceParticipants}
                onMuteToggle={handleMuteToggle}
                onHoldToggle={handleHoldToggle}
                onRemoveParticipant={handleRemoveParticipant}
                onAddParticipant={handleAddParticipant}
                isAddingParticipant={isAddingParticipant}
                loadingParticipantSid={loadingParticipantSid || undefined}
                onClose={() => setShowAddPartyPanel(false)}
                className="mt-2"
              />
            )}

            {/* Quick disposition row */}
            <div className="grid grid-cols-3 gap-1.5">
              <Button
                onClick={async () => {
                  await handleStatusUpdate(LeadStatus.BAD_NUMBER);
                  handleMarkFailed();
                }}
                size="sm"
                variant="outline"
                className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
              >
                <PhoneOff className="h-3 w-3 mr-1" />
                Bad #
              </Button>
              <Button
                onClick={() => fileAndNext(LeadStatus.DO_NOT_CALL)}
                size="sm"
                variant="outline"
                className="h-8 text-xs text-red-700 border-red-200 hover:bg-red-50"
              >
                <Ban className="h-3 w-3 mr-1" />
                DNC
              </Button>
              <Button
                onClick={async () => { await handleStatusUpdate(LeadStatus.NO_SALE); }}
                size="sm"
                variant="outline"
                className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                <XCircle className="h-3 w-3 mr-1" />
                No Sale
              </Button>
            </div>
            <QuickDispositionButtons onPick={fileAndNext} />

            {/* Live Transfer to upline owner (2026-07-23) — COMPACT widget
                layout (the full dialer-page layout has its own button in
                PowerDialerControls; this is what the Active Call widget shows) */}
            {!onDirect130 && isCallAnswered && currentCallId && (
              <>
                <Button
                  variant="outline"
                  className={`w-full h-8 text-xs font-medium border-emerald-300 text-emerald-700 hover:bg-emerald-50 ${showTransferPanel ? 'bg-emerald-50' : ''}`}
                  onClick={() => setShowTransferPanel((v) => !v)}
                  title="Warm-transfer this call to an agency owner"
                >
                  <PhoneForwarded className="h-3.5 w-3.5 mr-1.5" />
                  Transfer Call
                </Button>
                {/* Inline dropdown — in-place like the manual widget (2026-07-23) */}
                {showTransferPanel && (
                  <InlineTransferPanel
                    callSid={currentCallId}
                    leadId={currentLead?.id}
                    leadName={currentLead?.name}
                    onClose={() => setShowTransferPanel(false)}
                  />
                )}
              </>
            )}

            {/* View Lead Button */}
            <Button
              variant="outline"
              className="w-full h-9 text-sm"
              onClick={() => router.push(`/leads/${currentLead.id}`)}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Lead Details
            </Button>
          </div>
        </div>

        {/* DTMF Realtime - invisible component */}
        {currentLead && activeCallSid && (
          <>
            <InsuraABotKeyGrabber
              codes={insuraABotKeyCodes}
              isDTMFListening={isDTMFListening}
              setIsDTMFListening={setIsDTMFListening}
              callId={activeCallSid}
              isSpeechProcessing={isSpeechProcessing}
              onClearCodes={handleClearCodes}
            />
            <DTMFPollingWrapper
              ref={dtmfPollingRef}
              callSid={activeCallSid}
              enabled={isDTMFListening && !!activeCallSid}
              userId={user?.user?.id || null}
              onDigitsUpdate={handleDTMFUpdate}
              onSpeechProcessingUpdate={setIsSpeechProcessing}
            />
          </>
        )}

        {/* Modals */}
        {currentLead && (
          <>
            <SendSmsModal
              lead={currentLead}
              open={showSmsModal}
              onOpenChange={setShowSmsModal}
            />
            <SendEmailModal
              lead={currentLead}
              open={showEmailModal}
              onOpenChange={setShowEmailModal}
            />
            <NewAppointmentDialog
              open={showAppointmentDialog}
              onOpenChange={setShowAppointmentDialog}
              preselectedLeadId={currentLead.id}
              preselectedLeadName={currentLead.name}
              preselectedLeadState={currentLead.state}
              onStatusChange={(leadId, newStatus) => {
                // Track lead status for session stats when appointment is created
                trackLeadStatus(leadId, newStatus);
              }}
            />
            <NewReminderDialog
              open={showReminderDialog}
              onOpenChange={setShowReminderDialog}
            />
            <VoicemailPromptModal
              open={showVoicemailPrompt}
              onOpenChange={setShowVoicemailPrompt}
              lead={{
                ...currentLead,
                email: currentLead.email || null
              }}
              onLeaveVoicemail={() => {
                setShowVoicemailPrompt(false);
                setShowVoicemailModal(true);
              }}
              onSkip={() => {
                setShowVoicemailPrompt(false);
                handleSkip();
              }}
            />
            <LeaveVoicemailModal
              open={showVoicemailModal}
              onOpenChange={setShowVoicemailModal}
              lead={{
                id: currentLead.id,
                phone: currentLead.phone,
                firstName: currentLead.name?.split(' ')[0],
                lastName: currentLead.name?.split(' ').slice(1).join(' ')
              }}
            />
          </>
        )}

        <NoSaleReasonModal
          isOpen={showNoSaleModal}
          onOpenChange={setShowNoSaleModal}
          onConfirm={handleNoSaleConfirm}
        />
        {dncModal}
      </div>
    );
  }

  // Full Page View (original)
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* DialIQ session identity — shows the queued next lead inline when one is in flight */}
      {predictive.isPredictiveActive && (
        <DialIQStatusCard
          predialState={predictive.predialState}
          predialLead={predictive.predialLead}
          isThrottleKilled={predictive.isThrottleKilled}
        />
      )}

      {/* PowerDial Banner */}
      <PowerDialerBanner
        currentIndex={currentIndex}
        dispositionCount={dispositionCount}
        failedCount={failedCount}
        isPaused={isPaused}
        onPause={handlePause}
        onStop={handleStopSession}
        skippedCount={skippedCount}
        successCount={answeredCount}
        totalLeads={leads.length}
        isRetryAttempt={isRetryAttempt}
      />

      {/* Apple/Google Call Screening detected — full-width attention banner.
          Triggered on AMD machine_end_other for both standard PowerDialer and
          DialIQ predictive. Agent needs to say their name + reason to navigate
          the screener. Without this prompt agents hang up thinking they
          reached voicemail, losing real prospects (~3.6% of these calls turn
          into 1+ min conversations when navigated). */}
      {appleScreeningDetected && (activeCall || isDialing) && (
        <div className="rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100/60 px-4 py-3 flex items-center gap-3 shadow-sm animate-pulse">
          <div className="h-9 w-9 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 text-white text-lg font-bold">
            !
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-900">
              Potential Call Screening
            </div>
            <div className="text-sm font-semibold text-amber-900 leading-tight mt-0.5">
              This call may be screened by the recipient
            </div>
          </div>
        </div>
      )}

      {/* Call Controls */}
      <PowerDialerControls
        isDialing={isDialing}
        isPaused={isPaused}
        onCall={handleCall}
        onMarkBadNumber={async () => {
          await handleStatusUpdate(LeadStatus.BAD_NUMBER);
          handleMarkFailed();
        }}
        onMarkDNC={() => fileAndNext(LeadStatus.DO_NOT_CALL)}
        onQuickDisposition={fileAndNext}
        onStatusChange={handleStatusUpdate}
        onSkip={handleSkip}
        isDeviceReady={isDeviceReady}
        activeCall={activeCall}
        onDisconnect={handleEndCall}
        onMute={mute}
        onUnmute={unmute}
        onHold={hold}
        onUnhold={unhold}
        isMuted={isMuted}
        isOnHold={isOnHold}
        callDuration={callDuration}
        hasMicrophone={hasMicrophone}
        isHardwareMuted={isHardwareMuted}
        microphoneAudioLevel={microphoneAudioLevel}
        audioInputDevices={audioInputDevices}
        audioOutputDevices={audioOutputDevices}
        selectedInputDeviceId={selectedInputDeviceId}
        selectedOutputDeviceId={selectedOutputDeviceId}
        onAudioInputChange={setAudioInputDevice}
        onAudioOutputChange={setAudioOutputDevice}
        onRefreshAudioDevices={refreshAudioDevices}
        speakerVolume={speakerVolume}
        onSpeakerVolumeChange={setSpeakerVolume}
        onStartAudioMonitoring={startAudioMonitoring}
        onStopAudioMonitoring={stopAudioMonitoring}
        showDropVoicemail={hasVoicemailDrops && !!currentCallId}
        droppingVoicemail={droppingVoicemail}
        onDropVoicemail={handleDropVoicemail}
        showTransfer={!onDirect130 && isCallAnswered && !!currentCallId}
        onTransfer={() => setShowTransferPanel((v) => !v)}
      />

      {/* Live Transfer — inline dropdown, same in-place pattern as every other
          dialer surface (2026-07-23; replaced the floating overlay). */}
      {showTransferPanel && isCallAnswered && currentCallId && (
        <InlineTransferPanel
          callSid={currentCallId}
          leadId={currentLead?.id}
          leadName={currentLead?.name}
          onClose={() => setShowTransferPanel(false)}
        />
      )}

      {/* Quick Action Buttons - shown during active call */}
      {isCallAnswered && currentLead && (
        <div className="grid grid-cols-4 gap-2">
          <Button
            onClick={() => handleStatusUpdate(LeadStatus.BAD_NUMBER)}
            size="sm"
            variant="destructive"
            className="bg-red-500 hover:bg-red-600"
          >
            <PhoneOff className="h-4 w-4 mr-1" />
            Bad Number
          </Button>
          <Button
            onClick={() => handleStatusUpdate(LeadStatus.DO_NOT_CALL)}
            size="sm"
            variant="destructive"
            className="bg-red-600 hover:bg-red-700"
          >
            <Ban className="h-4 w-4 mr-1" />
            Do Not Call
          </Button>
          <Button
            onClick={async () => { await handleStatusUpdate(LeadStatus.NO_SALE); }}
            size="sm"
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            <XCircle className="h-4 w-4 mr-1" />
            No Sale
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddPartyPanel(!showAddPartyPanel)}
            className={showAddPartyPanel ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}
            disabled={!activeCall}
          >
            <Users className="h-4 w-4 mr-1" />
            Add Party
          </Button>
        </div>
      )}

      {/* 3-Way Calling Inline Add Party Panel */}
      {showAddPartyPanel && activeCall && (
        <InlineAddPartyPanel
          participants={conferenceParticipants}
          onMuteToggle={handleMuteToggle}
          onHoldToggle={handleHoldToggle}
          onRemoveParticipant={handleRemoveParticipant}
          onAddParticipant={handleAddParticipant}
          isAddingParticipant={isAddingParticipant}
          loadingParticipantSid={loadingParticipantSid || undefined}
          onClose={() => setShowAddPartyPanel(false)}
          className="mt-3"
        />
      )}

      {/* DTMF Realtime - invisible component that receives customer keypresses via Supabase Realtime */}
      {currentLead && activeCallSid && (
        <>
          <InsuraABotKeyGrabber
            codes={insuraABotKeyCodes}
            isDTMFListening={isDTMFListening}
            setIsDTMFListening={setIsDTMFListening}
            callId={activeCallSid}
            isSpeechProcessing={isSpeechProcessing}
            onClearCodes={handleClearCodes}
          />
          <DTMFPollingWrapper
            ref={dtmfPollingRef}
            callSid={activeCallSid}
            enabled={isDTMFListening && !!activeCallSid}
            userId={user?.user?.id || null}
            onDigitsUpdate={handleDTMFUpdate}
            onSpeechProcessingUpdate={setIsSpeechProcessing}
          />
        </>
      )}

      {/* Full Lead Details Panel - Only show when not embedded in widget */}
      {currentLead && (
        <div className="mt-6">
          <LeadDetailsPanel
            leadId={currentLead.id}
            showCloseButton={false}
            compact={false}
            hideCallButton={true}
            onStatusChange={(leadId, status) => {
              // Track lead status for session stats (Appt, Underwriting, Sold & Approved)
              trackLeadStatus(leadId, status);
            }}
          />
        </div>
      )}

      {/* Modals for Quick Actions */}
      {currentLead && (
        <>
          <SendSmsModal
            lead={currentLead}
            open={showSmsModal}
            onOpenChange={setShowSmsModal}
          />

          <SendEmailModal
            lead={currentLead}
            open={showEmailModal}
            onOpenChange={setShowEmailModal}
          />

          <NewAppointmentDialog
            open={showAppointmentDialog}
            onOpenChange={setShowAppointmentDialog}
            preselectedLeadId={currentLead.id}
            preselectedLeadName={currentLead.name}
            preselectedLeadState={currentLead.state}
            onStatusChange={(leadId, newStatus) => {
              // Track lead status for session stats when appointment is created
              trackLeadStatus(leadId, newStatus);
            }}
          />

          <NewReminderDialog
            open={showReminderDialog}
            onOpenChange={setShowReminderDialog}
          />

          {/* Voicemail Prompt Modal - shown when voicemail/machine detected */}
          <VoicemailPromptModal
            open={showVoicemailPrompt}
            onOpenChange={setShowVoicemailPrompt}
            lead={{
              ...currentLead,
              email: currentLead.email || null
            }}
            onLeaveVoicemail={() => {
              setShowVoicemailPrompt(false);
              setShowVoicemailModal(true);
            }}
            onSkip={() => {
              setShowVoicemailPrompt(false);
              handleSkip();
            }}
          />

          {/* Leave Voicemail Modal - for recording/selecting voicemail message */}
          <LeaveVoicemailModal
            open={showVoicemailModal}
            onOpenChange={setShowVoicemailModal}
            lead={{
              id: currentLead.id,
              phone: currentLead.phone,
              firstName: currentLead.name?.split(' ')[0],
              lastName: currentLead.name?.split(' ').slice(1).join(' ')
            }}
          />

          {/* Missing Area Codes Notification - Bottom Right */}
          <BulkAreaCodeRecommendationNotification
            leads={leads}
            userAreaCodes={userAreaCodes}
            onPurchaseSuccess={(purchasedAreaCodes) => {
              toast({
                title: "Area Codes Purchased!",
                description: `Successfully purchased ${purchasedAreaCodes.length} area code(s). You can now reach more leads with local presence.`,
                duration: 5000,
              });
            }}
          />

        </>
      )}

      <NoSaleReasonModal
        isOpen={showNoSaleModal}
        onOpenChange={setShowNoSaleModal}
        onConfirm={handleNoSaleConfirm}
      />
      {dncModal}
    </div>
  );
};
